import type { ReactNode } from "react";
import type { TFunction } from "i18next";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePickerField } from "@/components/shared/DatePickerField";
import type { Client } from "@/services/clientService";

const VALID_PERIODS = ["24h", "7d", "month", "last_month", "custom"] as const;

export type InvoicesListFiltersProps = {
  t: TFunction;
  periodMonthLabels: { thisMonthName: string; lastMonthName: string };
  period: string;
  status: string | undefined;
  clientId: string | undefined;
  currency: string | undefined;
  createdByUserId: string | undefined;
  dateFrom: string | undefined;
  dateTo: string | undefined;
  clients: Client[];
  users: Array<{ userId: string; displayName: string | null; email: string }>;
  setFilter: (key: "status" | "period" | "client" | "currency" | "createdByUserId", value: string) => void;
  setCustomDateRange: (from: string, to: string) => void;
  getCurrentMonthRange: () => { dateFrom: string; dateTo: string };
  clearFiltersButton?: ReactNode;
  className?: string;
  /**
   * "row" (default) — inline horizontal with narrow selects, used in the page bar.
   * "stacked" — label as small mono caps above a full-width select, used in the modal.
   */
  layout?: "row" | "stacked";
};

export function InvoicesListFilters({
  t,
  periodMonthLabels,
  period,
  status,
  clientId,
  currency,
  createdByUserId,
  dateFrom,
  dateTo,
  clients,
  users,
  setFilter,
  setCustomDateRange,
  getCurrentMonthRange,
  clearFiltersButton,
  className,
  layout = "row",
}: InvoicesListFiltersProps): React.JSX.Element {
  const defaultRange = getCurrentMonthRange();
  const stacked = layout === "stacked";

  const rowClass = stacked
    ? "flex w-full flex-col gap-1.5"
    : "flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2";
  const labelClass = stacked
    ? "text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--fx-ink-500)] dark:text-[var(--dk-text-mute)]"
    : "shrink-0 text-sm text-muted-foreground";

  return (
    <div
      className={
        className ??
        "flex flex-wrap items-center gap-x-4 gap-y-3"
      }
    >
      {/* Período — own row */}
      <div className={rowClass}>
        <span className={labelClass}>
          {t("invoices.list.filterPeriod", "Date range")}
        </span>
        <Select value={period} onValueChange={(v) => setFilter("period", v)}>
          <SelectTrigger className={stacked ? "w-full" : "w-full sm:w-[160px]"}>
            <SelectValue placeholder={t("invoices.list.filterPeriod", "Período")}>
              {period === "month"
                ? periodMonthLabels.thisMonthName.charAt(0).toUpperCase() + periodMonthLabels.thisMonthName.slice(1)
                : period === "last_month"
                  ? periodMonthLabels.lastMonthName.charAt(0).toUpperCase() + periodMonthLabels.lastMonthName.slice(1)
                  : period === "24h"
                    ? t("invoices.list.period24h", "Últimas 24h")
                    : period === "7d"
                      ? t("invoices.list.period7d", "Últimos 7 días")
                      : period === "custom"
                        ? t("invoices.list.periodCustom", "Personalizado")
                        : null}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">{t("invoices.list.period24h", "Last 24 hours")}</SelectItem>
            <SelectItem value="7d">{t("invoices.list.period7d", "Last 7 days")}</SelectItem>
            <SelectItem value="month">
              {t("invoices.list.periodMonthWithName", { month: periodMonthLabels.thisMonthName })}
            </SelectItem>
            <SelectItem value="last_month">
              {t("invoices.list.periodLastMonthWithName", { month: periodMonthLabels.lastMonthName })}
            </SelectItem>
            <SelectItem value="custom">{t("invoices.list.periodCustom", "Custom")}</SelectItem>
          </SelectContent>
        </Select>
        {period === "custom" && (
          <div className="flex flex-wrap items-end gap-4 pl-1">
            <DatePickerField
              value={dateFrom ?? defaultRange.dateFrom}
              onChange={(v) =>
                setCustomDateRange(v, dateTo ?? defaultRange.dateTo)
              }
              label={t("invoices.list.filterDateFrom", "From")}
              labelPosition="inline"
              triggerClassName="pr-3"
              className="min-w-0 w-full sm:w-auto"
            />
            <DatePickerField
              value={dateTo ?? defaultRange.dateTo}
              onChange={(v) =>
                setCustomDateRange(dateFrom ?? defaultRange.dateFrom, v)
              }
              label={t("invoices.list.filterDateTo", "To")}
              labelPosition="inline"
              triggerClassName="pr-3"
              className="min-w-0 w-full sm:w-auto"
            />
          </div>
        )}
      </div>

      {/* Remaining filters — wrap naturally */}
      <div className={rowClass}>
        <span className={labelClass}>
          {t("invoices.list.filterStatus", "Status")}
        </span>
        <Select
          value={status ?? "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className={stacked ? "w-full" : "w-full sm:w-[160px]"}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("invoices.list.statusAll", "All statuses")}</SelectItem>
            <SelectItem value="draft">{t("invoices.list.statusDraft", "Drafts")}</SelectItem>
            <SelectItem value="accepted">{t("invoices.list.statusAccepted", "Accepted")}</SelectItem>
            <SelectItem value="pending">{t("invoices.list.statusPending", "Pending")}</SelectItem>
            <SelectItem value="rejected">{t("invoices.list.statusRejected", "Rejected")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className={rowClass}>
        <span className={labelClass}>
          {t("invoices.list.filterClient", "Client")}
        </span>
        <Select
          value={clientId ?? "all"}
          onValueChange={(v) => setFilter("client", v === "all" ? "" : v)}
        >
          <SelectTrigger className={stacked ? "w-full" : "w-full min-w-0 sm:w-[180px]"}>
            <SelectValue placeholder={t("invoices.list.clientAll", "All clients")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("invoices.list.clientAll", "All clients")}</SelectItem>
            <SelectItem value="none">
              {t("invoices.list.consumerFinal", "Consumidor final")}
            </SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.legalName ?? c.displayName ?? c.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {users.length > 1 && (
        <div className={rowClass}>
          <span className={labelClass}>
            {t("invoices.list.createdBy")}
          </span>
          <Select
            value={createdByUserId ?? "all"}
            onValueChange={(v) => setFilter("createdByUserId", v === "all" ? "" : v)}
          >
            <SelectTrigger className={stacked ? "w-full" : "w-full min-w-0 sm:w-[180px]"}>
              <SelectValue placeholder={t("invoices.list.allUsers")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("invoices.list.allUsers")}</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.userId} value={u.userId}>
                  {u.displayName?.trim() || u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className={rowClass}>
        <span className={labelClass}>
          {t("invoices.list.filterCurrency", "Currency")}
        </span>
        <Select
          value={currency ?? "all"}
          onValueChange={(v) => setFilter("currency", v === "all" ? "" : v)}
        >
          <SelectTrigger className={stacked ? "w-full" : "w-full sm:w-[100px]"}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("invoices.list.currencyAll", "All")}</SelectItem>
            <SelectItem value="UYU">UYU</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {clearFiltersButton}
    </div>
  );
}

export { VALID_PERIODS };
